import { Component } from '@theme/component';
import { promoteVideoSources } from '@theme/video-sources';

/**
 * Motion panel — the script half of `sections/motion-panel.liquid`.
 *
 * **Nothing downloads until the panel is nearly on screen.** The section writes every video URL
 * into `data-video-source` and none into `src`, so an edit with four panels costs a visitor who
 * reads only the first thread one poster image per panel and nothing more. Without this script the
 * panel is its poster, which is the same thing a reduced-motion visitor sees on purpose.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} [video] - Absent while the merchant has not picked a file yet, in
 *   which case the panel is a placeholder and there is nothing to do.
 * @property {HTMLElement[]} sources - Deferred `<source>` elements, tagged by viewport when the
 *   section carries a second file for mobile.
 * @property {HTMLButtonElement} [toggle] - Pause and play. Required by WCAG 2.2.2 for motion that
 *   starts on its own and runs past five seconds, which every loop does.
 *
 * @extends Component<Refs>
 */
export class MotionPanel extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {MediaQueryList | undefined} */
  #mobileQuery;

  /** Whether the sources have been given real URLs yet. */
  #hydrated = false;

  #onScreen = false;

  /**
   * Set when the visitor presses pause, so scrolling away and back does not start it again. A
   * pause that undoes itself is not a pause.
   */
  #pausedByVisitor = false;

  connectedCallback() {
    super.connectedCallback();

    // The stylesheet hides the video and the button under reduced motion, and the poster stays.
    // Nothing is fetched for a video nobody is going to see.
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!this.refs.video) return;

    const { mobileBreakpoint } = this.dataset;
    if (mobileBreakpoint) {
      this.#mobileQuery = matchMedia(mobileBreakpoint);
      this.#mobileQuery.addEventListener('change', this.#reselect);
    }

    this.#watchVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#mobileQuery?.removeEventListener('change', this.#reselect);
    this.#mobileQuery = undefined;
  }

  /**
   * Bound to the pause button with `on:click`.
   *
   * @returns {void}
   */
  toggle() {
    this.#pausedByVisitor = !this.#pausedByVisitor;
    this.#pausedByVisitor ? this.#pause() : this.#play();
  }

  /**
   * Starts loading a little before the panel arrives, so the first frame is usually ready by the
   * time anyone sees it, and stops decoding the moment it leaves. A long edit is mostly panels
   * nobody is looking at.
   *
   * @returns {void}
   */
  #watchVisibility() {
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.#onScreen = entry.isIntersecting;

        if (this.#onScreen && !this.#hydrated) this.#hydrate();

        if (!this.#onScreen) this.#pause();
        else if (!this.#pausedByVisitor) this.#play();
      },
      { rootMargin: '200px 0px' }
    );

    this.#observer.observe(this);
  }

  /** @returns {void} */
  #hydrate() {
    const { video, sources } = this.refs;
    if (!video || !sources?.length) return;

    promoteVideoSources(sources, !!this.#mobileQuery?.matches);
    video.load();
    this.#hydrated = true;
  }

  /**
   * A phone turned on its side crosses the breakpoint, and so does the theme editor switching
   * between its two previews. A panel that has not loaded yet has nothing to swap.
   *
   * @type {() => void}
   */
  #reselect = () => {
    if (!this.#hydrated) return;

    this.#hydrate();
    if (this.#onScreen && !this.#pausedByVisitor) this.#play();
  };

  /** @returns {void} */
  #play() {
    // Autoplay can be refused. A muted, inline video rarely is, but an uncaught rejection is still
    // noise in the console and there is nothing to recover.
    this.refs.video?.play().catch(() => {});
    this.#reflect();
  }

  /** @returns {void} */
  #pause() {
    this.refs.video?.pause();
    this.#reflect();
  }

  /**
   * The button reports the visitor's choice, not whether a frame is moving. Scrolling the panel
   * off screen pauses the video, but nobody pressed anything, so the button does not change.
   *
   * @returns {void}
   */
  #reflect() {
    const { toggle } = this.refs;
    toggle?.setAttribute('aria-pressed', String(this.#pausedByVisitor));
    this.toggleAttribute('paused', this.#pausedByVisitor);
  }
}

if (!customElements.get('motion-panel')) {
  customElements.define('motion-panel', MotionPanel);
}
